/**
 * Where a provider sign-in lands (the server redirects to /<lang>?oauth=new, /<lang>?oauth=undeleted — the sign-in
 * cancelled a pending account deletion —, /<lang>/profile?oauth=linked | reauth or …?oauthError=<CODE>): one toast,
 * then the parameters leave the address bar.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ERROR_CODES, type ErrorCode } from '@tg/shared';
import { paths } from '@/app/paths';
import type { Locale } from '@/i18n/locales';
import { useLocale } from '@/i18n/useLocale';
import { useSession } from '@/stores/session';
import { toast } from '@/stores/toasts';

/** a new account from a provider: the nickname was made up for the player — say where to change it */
export function welcomeNewAccount(nickname: string, l: Locale): void {
  toast.link('auth.oauth.welcome', { to: paths.profile(l), labelId: 'auth.oauth.changeNickname' }, { nickname });
}

export function useOAuthLanding(): void {
  const loc = useLocation();
  const navigate = useNavigate();
  const l = useLocale();
  const me = useSession((st) => st.me);
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const ok = q.get('oauth');
    const err = q.get('oauthError');
    if (!ok && !err) return;
    // the sign-in screen shows its error itself (AuthPage)
    if (/\/(signin|signup)$/.test(loc.pathname)) return;
    // the welcome needs the nickname: wait until the session is loaded
    if (ok === 'new' && !me) return;
    if (ok === 'new' && me) welcomeNewAccount(me.nickname, l);
    else if (ok === 'linked') toast.success('profile.methods.linked');
    else if (ok === 'reauth') toast.success('profile.reauth.done');
    else if (ok === 'undeleted') toast.success('profile.deletion.cancelledBySignIn');
    if (err && (ERROR_CODES as readonly string[]).includes(err)) toast.code(err as ErrorCode);
    q.delete('oauth');
    q.delete('oauthError');
    const rest = q.toString();
    navigate({ pathname: loc.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
  }, [loc.pathname, loc.search, me, l, navigate]);
}

/** mounted once in the app chrome (App.tsx) */
export function OAuthLanding(): null {
  useOAuthLanding();
  return null;
}
